import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const CHAPTERS = [
  {
    no: "01",
    title: "We design feelings, not just venues.",
    body: "Every celebration begins with a story. We translate your emotions, culture and dreams into a cohesive visual language — from the palette to the smallest floral detail.",
  },
  {
    no: "02",
    title: "Obsessed with the details you never notice.",
    body: "The tilt of a candle, the warmth of a spotlight, the exact moment the music swells. Our teams sweat the invisible so your guests only feel the magic.",
  },
  {
    no: "03",
    title: "One team, the entire experience.",
    body: "Planning, decor, lighting, entertainment and luxury transport — orchestrated under one roof. No juggling vendors, only a single flawless day that feels effortless.",
  },
];

export const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 25]);

  return (
    <section id="about" ref={ref} className="relative overflow-hidden bg-[#0D0C0A] py-24 text-[#FCFBF8] sm:py-32 lg:py-40">
      <div className="mx-auto max-w-[1500px] px-6 sm:px-12 lg:px-16">
        <div className="mb-16 flex items-center gap-4">
          <span className="h-px w-14 bg-[#C5A059]" />
          <span className="font-body text-xs font-semibold uppercase tracking-[0.3em] text-[#C5A059]">
            Our Philosophy
          </span>
        </div>

        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-24">
          {/* Sticky rotating ornament */}
          <div className="relative hidden lg:col-span-4 lg:block">
            <div className="sticky top-32">
              <motion.div style={{ y, rotate }} className="relative">
                <svg viewBox="0 0 240 240" className="h-72 w-72">
                  <defs>
                    <linearGradient id="mgold" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#EAD39C" />
                      <stop offset="100%" stopColor="#A37E3A" />
                    </linearGradient>
                  </defs>
                  {[...Array(12)].map((_, i) => (
                    <ellipse
                      key={i}
                      cx="120"
                      cy="120"
                      rx="26"
                      ry="112"
                      fill="none"
                      stroke="url(#mgold)"
                      strokeWidth="0.8"
                      opacity="0.55"
                      transform={`rotate(${i * 15} 120 120)`}
                    />
                  ))}
                  <circle cx="120" cy="120" r="12" fill="url(#mgold)" />
                </svg>
              </motion.div>
              <p className="mt-10 max-w-xs font-heading text-2xl italic leading-snug text-[#EAD39C]">
                “A celebration should feel like a memory even while it&apos;s happening.”
              </p>
            </div>
          </div>

          <div className="lg:col-span-8">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.no}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`manifesto-chapter-${c.no}`}
                className={`flex flex-col gap-6 border-t border-[#FCFBF8]/12 py-12 md:flex-row md:gap-12 ${
                  i === CHAPTERS.length - 1 ? "border-b" : ""
                }`}
              >
                <span className="font-heading text-6xl font-light text-transparent lg:text-8xl" style={{ WebkitTextStroke: "1px #C5A059" }}>
                  {c.no}
                </span>
                <div className="flex-1">
                  <h3 className="font-heading text-3xl font-light leading-tight text-[#FCFBF8] lg:text-5xl">
                    {c.title}
                  </h3>
                  <p className="mt-5 max-w-xl font-body text-base leading-relaxed text-[#FCFBF8]/60">
                    {c.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
